/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * useFilterState Hook
 *
 * Standardizes filter extraction and active filter count calculation
 * across all hub pages. Reduces boilerplate and ensures consistency.
 */

"use client";

import { useMemo } from "react";

/**
 * Configuration for useFilterState hook
 */
export interface UseFilterStateConfig<TFilterState> {
  /**
   * Current filter state object (typically from useStandardFilters)
   */
  filterState: TFilterState;

  /**
   * Search term (server-side debounced)
   */
  searchTerm?: string | null;

  /**
   * Additional filter keys to check (beyond filterState)
   * Each entry maps a filter key to its current value and default/empty value
   */
  additionalFilters?: Array<{
    key: string;
    value: unknown;
    defaultValue?: unknown;
  }>;
}

/**
 * Filter state information returned by useFilterState
 */
export interface FilterStateInfo<TFilterState> {
  /**
   * Current filter state (typed)
   */
  filters: TFilterState;

  /**
   * Search term (server-side debounced)
   */
  searchTerm: string | null;

  /**
   * Count of active filters (excluding defaults/empty values)
   */
  activeFilterCount: number;

  /**
   * Whether any filters are currently active
   */
  hasActiveFilters: boolean;

  /**
   * Helper to get a filter value with type safety
   */
  getFilter: <K extends keyof TFilterState>(
    key: K,
  ) => TFilterState[K] | undefined;
}

/**
 * Standardized hook for managing filter state and calculating active filter counts.
 *
 * Reduces boilerplate by providing typed filter accessors and automatic
 * active filter count calculation.
 *
 * @example
 * ```typescript
 * const { filterState, searchTermServer } = useStandardFilters({
 *   id: "clients",
 *   initialFilters: { status: "all", priority: "all" },
 * });
 *
 * const [assignedToFilter, setAssignedToFilter] = useState<string>("all");
 *
 * const filterInfo = useFilterState({
 *   filterState,
 *   searchTerm: searchTermServer,
 *   additionalFilters: [
 *     { key: "assignedTo", value: assignedToFilter, defaultValue: "all" },
 *   ],
 * });
 *
 * // Use filterInfo.activeFilterCount for badge display
 * // Use filterInfo.hasActiveFilters for conditional rendering
 * // Use filterInfo.getFilter("status") for typed access
 * ```
 */
export function useFilterState<TFilterState extends Record<string, unknown>>(
  config: UseFilterStateConfig<TFilterState>,
): FilterStateInfo<TFilterState> {
  const { filterState, searchTerm, additionalFilters = [] } = config;

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;

    // Count search term if present
    if (searchTerm && searchTerm.trim().length > 0) {
      count++;
    }

    // Count filterState values that are not default/empty
    // Skip infrastructure keys that are part of FilterState but not user-facing filters
    const infrastructureKeys = new Set(["search", "page", "pageSize"]);
    for (const [key, value] of Object.entries(filterState)) {
      if (infrastructureKeys.has(key)) continue;
      // Skip undefined, null, empty string, and "all" (common default)
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        value !== "all"
      ) {
        count++;
      }
    }

    // Count additional filters
    for (const filter of additionalFilters) {
      const { value, defaultValue = "all" } = filter;
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        value !== defaultValue
      ) {
        count++;
      }
    }

    return count;
  }, [filterState, searchTerm, additionalFilters]);

  // Helper to get filter value with type safety
  const getFilter = useMemo(
    () =>
      <K extends keyof TFilterState>(key: K): TFilterState[K] | undefined => {
        return filterState[key];
      },
    [filterState],
  );

  return useMemo(
    () => ({
      filters: filterState,
      searchTerm: searchTerm ?? null,
      activeFilterCount,
      hasActiveFilters: activeFilterCount > 0,
      getFilter,
    }),
    [filterState, searchTerm, activeFilterCount, getFilter],
  );
}
